import { Injectable, inject } from '@angular/core';
import {ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot} from '@angular/router';
import {Observable, of} from 'rxjs';
import {StorageService} from '../services/storage.service';
import {MemberStateService} from '../services/member-state.service';
import {UserContextService} from '../services/user-context.service';
import {MemberService} from '../services/member.service';
import {PlatformService} from '../auth/services/platform.service';
import {AuthService} from '../auth/services/auth.service';
import {take, map, catchError} from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class MemberIsPresentGuard implements CanActivate {
  private storage = inject(StorageService);
  private router = inject(Router);
  private memberStateService = inject(MemberStateService);
  private userContext = inject(UserContextService);
  private memberService = inject(MemberService);
  private platformService = inject(PlatformService);
  private authService = inject(AuthService);


  canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> | Promise<boolean> | boolean {

    this.memberStateService.setIsOpenedInNewTabSubject(false);

    const INTERNALUSER = this.userContext.isInternalUser();
    const URL = state.url;
    const PATH = next.routeConfig.path;

    if ((PATH === '**')) {
     // So we reroute them to the default URL ...
      if (INTERNALUSER) {
        this.router.navigate(['/search']).then().catch();                            // default for internal is "search",
      } else {
        this.router.navigate(['/membership']).then().catch();                        // default for external is "membership".
      }
      return true;
    }

    if (URL === '/search') {
      if (!INTERNALUSER) {
        this.router.navigate(['/membership']).then().catch();
        return false;
      } else {
        // or if internal, then off they go to search.
        return true;
      }
    }

    // You can only get to here with a valid URL from the app-routing.module.ts.

    if (this.storage.getMember() !== null) {
      // Ensure MemberStateService is in sync with storage
      // (e.g., after page reload, the BehaviorSubject is re-initialized from storage
      //  but setMember ensures both are consistent)
      try {
        const storedMember = JSON.parse(this.storage.getMember());
        this.memberStateService.setMember(storedMember);
      } catch (e) {
        console.error('[MemberIsPresentGuard] Failed to parse stored member:', e);
      }
      return true;
    } else {
      if (INTERNALUSER) {
        this.router.navigate(['/search']).then().catch();                              // redirect them to search for the member to create
        this.memberStateService.setIsOpenedInNewTabSubject(true);
        return false;
      } else {
        // No session in this tab while the Keycloak SSO session may still be alive (a link
        // opened in a new tab, Android's itsme handoff): log in again. Calling /members/me
        // without a token gets a 400 from Tyk, which this guard used to treat as fatal.
        if (!this.authService.hasValidAccessToken()) {
          if (this.platformService.isMobile()) {
            this.router.navigateByUrl('/auth/landing').then().catch();
          } else {
            this.authService.login(URL).catch(() => {
              window.location.href = '/assets/auth-failure.html';
            });
          }
          return false;
        }

        // EXTERNAL USERS: Jailed to their own data only
        // Use /members/me endpoint which Tyk rewrites to /members/{jwt.preferred_username}
        // No INSS parameter needed - Tyk extracts from JWT
        // Return an Observable that waits for the API call to complete.
        // The router holds navigation until this emits, avoiding
        // the fire-and-forget race condition on mobile WebViews.
        return this.memberService.getMyMember()
          .pipe(
            take(1),
            map(m => {
              this.memberStateService.setMember(m);
              return true;
            }),
            catchError(err => {
              console.error('[MemberIsPresentGuard] Failed to load member data:', err);
              console.error('[MemberIsPresentGuard] Status:', err.status);

              // Same handling as AuthService: on mobile stay inside the SPA and go
              // back to the landing page, on web hand over to the static bilingual
              // failure page. Leaving the SPA on mobile would strand the user in a
              // WebView with no way back.
              if (this.platformService.isMobile()) {
                this.router.navigateByUrl('/auth/landing').then().catch();
              } else {
                window.location.href = '/assets/auth-failure.html';
              }
              return of(false);
            })
          );
      }
    }
  }
}
